import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Apple, ArrowRight, CheckCircle2, Zap, Bell, BellRing, BellOff, ShieldCheck, Loader2, Moon, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import useWebPush from "@/hooks/useWebPush";

/**
 * /admin/install — Install the Command Center as a PWA.
 *
 * Two use-cases:
 *   1. Studio owner is at their laptop → shows a big QR code they scan with
 *      their phone. Phone opens /admin/install → gets an "Install App"
 *      button that fires the native browser install prompt.
 *   2. Owner is on their phone → same QR is hidden, big install CTA is shown
 *      instead (either the native prompt on Android/Chrome, or the manual
 *      "Add to Home Screen" walkthrough on iOS Safari).
 */
export default function AdminInstall() {
  const [canInstall, setCanInstall] = useState(!!window.sbDeferredPrompt);
  const [installed, setInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [platform, setPlatform] = useState("desktop"); // 'ios' | 'android' | 'desktop'
  const push = useWebPush();

  // Origin used inside the QR — matches whatever URL the user is already on.
  const installUrl = `${window.location.origin}/admin/install?src=qr`;

  useEffect(() => {
    const ua = navigator.userAgent || "";
    if (/iPhone|iPad|iPod/i.test(ua)) setPlatform("ios");
    else if (/Android/i.test(ua)) setPlatform("android");
    else setPlatform("desktop");

    // Detect if the current tab is running as an installed PWA
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const onCan = () => setCanInstall(true);
    const onInstalled = () => {
      setInstalled(true);
      setCanInstall(false);
      toast.success("Command Center installed. Look for the icon on your home screen.");
    };
    window.addEventListener("sb:pwa-installable", onCan);
    window.addEventListener("sb:pwa-installed", onInstalled);
    return () => {
      window.removeEventListener("sb:pwa-installable", onCan);
      window.removeEventListener("sb:pwa-installed", onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    const evt = window.sbDeferredPrompt;
    if (!evt) {
      toast.error("Your browser didn't offer an install prompt. Use the browser menu → 'Add to Home Screen'.");
      return;
    }
    evt.prompt();
    const result = await evt.userChoice;
    if (result?.outcome === "accepted") {
      setInstalled(true);
    }
    window.sbDeferredPrompt = null;
    setCanInstall(false);
  };

  const perks = [
    { icon: Zap, title: "1-tap open", body: "Launches from your home screen as a full-screen app — no browser chrome, no address bar." },
    { icon: BellRing, title: "Live push alerts", body: "Bookings, guest requests, and Monday events buzz your phone the moment they land — even if the app is closed." },
    { icon: ShieldCheck, title: "Locked to admin", body: "Only your admin login unlocks it — same auth, same data, mirrored 1:1 with the web dashboard." },
    { icon: CheckCircle2, title: "Offline shell", body: "Even without signal, the app loads instantly. Data syncs the moment you're back online." },
  ];

  const togglePush = async () => {
    if (push.subscribed) {
      await push.disable();
      toast.success("Notifications turned off on this device.");
    } else {
      const ok = await push.enable();
      if (ok) toast.success("You're wired up. Watch for the test — you can send one below.");
    }
  };

  const runTest = async () => {
    const res = await push.sendTest();
    if (res?.sent > 0) toast.success(`Test sent to ${res.sent} device${res.sent > 1 ? "s" : ""}.`);
    else if (res?.skipped) toast.info("Test skipped by quiet hours (should never happen — reach out if this repeats).");
    else if (res) toast.error("Server accepted the request, but no devices responded.");
  };

  const quiet = push.status?.quiet_hours;
  const inQuiet = !!push.status?.in_quiet_hours_now;

  return (
    <div className="min-h-screen bg-[#05050a] text-white font-sans">
      {/* Ambient background glow */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#00f0ff]/10 blur-[120px]" />
        <div className="absolute top-1/2 -right-40 w-[520px] h-[520px] rounded-full bg-[#ff0055]/8 blur-[120px]" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Top row */}
        <div className="flex items-center justify-between mb-10 md:mb-14">
          <Link
            to="/admin"
            data-testid="install-back-admin"
            className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 transition-colors"
          >
            ← Back to Command Center
          </Link>
          <div className="font-mono-tight text-[10px] uppercase tracking-[0.3em] text-white/40">
            v1.0 · PWA
          </div>
        </div>

        {/* Hero */}
        <div className="mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold border border-[#00f0ff]/40 bg-[#00f0ff]/10 text-[#00f0ff] mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00f0ff] animate-pulse" />
            Install on your phone
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] max-w-3xl">
            Turn <span className="text-[#00f0ff]">Command Center</span> into a real app on your phone.
          </h1>
          <p className="text-[#a395c3] text-base md:text-lg mt-5 max-w-2xl leading-relaxed">
            Everything you can do from the web — bookings, guest requests, Monday events, marketing CRM, digest — installed as a standalone app on your home screen. No app store. No install fees. Just scan &amp; go.
          </p>
        </div>

        {/* Notifications toggle — the promised phone-buzz feature */}
        <div
          data-testid="push-toggle-card"
          className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-[#00f0ff]/[0.06] via-white/[0.02] to-transparent backdrop-blur-md p-6 md:p-8 mb-10"
        >
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex items-start gap-4 flex-1 min-w-0">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 border ${push.subscribed ? "bg-[#00f0ff]/15 border-[#00f0ff]/40" : "bg-white/[0.05] border-white/15"}`}>
                {push.subscribed ? (
                  <BellRing size={26} className="text-[#00f0ff]" strokeWidth={2} />
                ) : (
                  <BellOff size={26} className="text-white/60" strokeWidth={2} />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display font-bold text-xl">Push notifications</h3>
                  {push.subscribed ? (
                    <span className="text-[10px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-full bg-[#22c55e]/15 text-[#22c55e] border border-[#22c55e]/30">
                      On
                    </span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.24em] px-2 py-0.5 rounded-full bg-white/[0.05] text-white/50 border border-white/15">
                      Off
                    </span>
                  )}
                </div>
                <p className="text-[#a395c3] text-sm mt-1.5 leading-relaxed">
                  {push.subscribed
                    ? "This device is subscribed. New bookings + Spotlight guests will buzz you the moment they land — even with the app closed."
                    : "Enable push and your phone will buzz for every new booking, guest request, contact, or Monday event."}
                </p>
                {quiet && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-white/50">
                    <Moon size={12} className={inQuiet ? "text-[#00f0ff]" : ""} />
                    <span>
                      Quiet hours: {String(quiet.start).padStart(2, "0")}:00 – {String(quiet.end).padStart(2, "0")}:00 · {quiet.tz}
                      {inQuiet && <span className="text-[#00f0ff] ml-2">· active now</span>}
                    </span>
                  </div>
                )}
                {push.error && (
                  <div
                    data-testid="push-error"
                    className="mt-3 flex items-start gap-2 text-xs text-[#ff6b7a] bg-[#ff0055]/10 border border-[#ff0055]/30 rounded-lg px-3 py-2"
                  >
                    <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
                    <span>{push.error}</span>
                  </div>
                )}
                {!push.supported && (
                  <p className="mt-3 text-xs text-white/50">
                    This browser can&rsquo;t receive push. Install the app on your phone (via the QR below) and enable notifications there.
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-3 self-start md:self-auto flex-shrink-0">
              {push.subscribed && (
                <button
                  type="button"
                  onClick={runTest}
                  disabled={push.busy}
                  data-testid="push-test-btn"
                  className="px-4 py-3 rounded-full bg-white/[0.05] border border-white/15 text-sm font-medium hover:bg-white/[0.1] transition-colors disabled:opacity-50"
                >
                  Send test
                </button>
              )}
              <button
                type="button"
                onClick={togglePush}
                disabled={push.busy || !push.supported}
                data-testid="push-toggle-btn"
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-all disabled:opacity-50 ${
                  push.subscribed
                    ? "bg-white/[0.05] border border-white/15 text-white/85 hover:bg-white/[0.12]"
                    : "bg-[#00f0ff] text-[#05050a] hover:bg-white"
                }`}
              >
                {push.busy ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Working…
                  </>
                ) : push.subscribed ? (
                  <>
                    <BellOff size={14} />
                    Turn off
                  </>
                ) : (
                  <>
                    <BellRing size={14} />
                    Enable notifications
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Content grid */}
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-start">
          {/* Left — QR + install actions */}
          <div className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-6 md:p-10">
            {isStandalone ? (
              <div
                data-testid="install-standalone-notice"
                className="text-center py-8"
              >
                <CheckCircle2 className="mx-auto text-[#00f0ff] mb-4" size={48} />
                <div className="font-display font-bold text-2xl mb-2">You&rsquo;re already in.</div>
                <p className="text-[#a395c3] text-sm">Command Center is running as an installed app. You&rsquo;re looking at it right now.</p>
              </div>
            ) : platform === "desktop" ? (
              <>
                <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-3">
                  Step 1 · Scan with your phone
                </div>
                <div className="bg-white p-5 rounded-2xl inline-block" data-testid="install-qr">
                  <QRCodeSVG
                    value={installUrl}
                    size={220}
                    level="H"
                    marginSize={0}
                    fgColor="#05050a"
                    bgColor="#ffffff"
                    imageSettings={{
                      src: "/pwa/icon-192.png",
                      height: 42,
                      width: 42,
                      excavate: true,
                    }}
                  />
                </div>
                <div className="mt-6 space-y-3">
                  <div className="flex items-start gap-3 text-sm">
                    <span className="w-6 h-6 rounded-full bg-[#00f0ff]/15 text-[#00f0ff] flex items-center justify-center text-xs font-semibold flex-shrink-0">1</span>
                    <span className="text-white/85">Point your phone camera at the QR code above.</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <span className="w-6 h-6 rounded-full bg-[#00f0ff]/15 text-[#00f0ff] flex items-center justify-center text-xs font-semibold flex-shrink-0">2</span>
                    <span className="text-white/85">Tap the link. Command Center opens in Safari or Chrome.</span>
                  </div>
                  <div className="flex items-start gap-3 text-sm">
                    <span className="w-6 h-6 rounded-full bg-[#00f0ff]/15 text-[#00f0ff] flex items-center justify-center text-xs font-semibold flex-shrink-0">3</span>
                    <span className="text-white/85">Tap the big <b>Install App</b> button on the mobile page.</span>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-white/10 text-xs text-white/50 break-all">
                  Direct link: <span className="text-white/70">{installUrl}</span>
                </div>
              </>
            ) : (
              /* Mobile — the actual install path */
              <div className="text-center">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.28em] font-semibold border border-white/20 bg-white/[0.06] text-white/70 mb-6">
                  {platform === "ios" ? <Apple size={12} /> : <Smartphone size={12} />}
                  {platform === "ios" ? "iPhone / iPad" : "Android"}
                </div>

                {platform === "android" ? (
                  <>
                    <button
                      type="button"
                      onClick={triggerInstall}
                      data-testid="install-native-btn"
                      disabled={!canInstall}
                      className={`w-full inline-flex items-center justify-center gap-2 py-5 rounded-2xl font-semibold text-base transition-all ${
                        canInstall
                          ? "bg-[#00f0ff] text-[#05050a] hover:bg-white active:scale-[0.98]"
                          : "bg-white/[0.06] text-white/40 border border-white/10"
                      }`}
                    >
                      {canInstall ? (
                        <>
                          <Smartphone size={18} /> Install Command Center
                          <ArrowRight size={16} />
                        </>
                      ) : (
                        <>Preparing install…</>
                      )}
                    </button>
                    {!canInstall && !installed && (
                      <p className="text-xs text-white/50 mt-3 leading-relaxed">
                        Your browser will offer the install option in a few seconds. Or open the Chrome menu (⋮) → <b>Install app</b>.
                      </p>
                    )}
                    {installed && (
                      <p
                        data-testid="install-success"
                        className="text-sm text-[#00f0ff] mt-4 inline-flex items-center gap-2 justify-center"
                      >
                        <CheckCircle2 size={16} /> Installed — check your home screen.
                      </p>
                    )}
                  </>
                ) : (
                  /* iOS manual walkthrough */
                  <div className="text-left">
                    <div className="text-white/80 mb-4 text-sm">
                      Safari doesn&rsquo;t offer an automatic install — but two taps get you there:
                    </div>
                    <ol className="space-y-3 text-sm">
                      <li className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-full bg-[#00f0ff]/15 text-[#00f0ff] flex items-center justify-center text-sm font-semibold flex-shrink-0">1</span>
                        <span className="text-white/85">
                          Tap the <b>Share</b> icon in Safari&rsquo;s bottom bar
                          <span className="inline-block ml-1 px-2 py-0.5 rounded bg-white/10 text-white/60 text-xs">↑</span>
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-full bg-[#00f0ff]/15 text-[#00f0ff] flex items-center justify-center text-sm font-semibold flex-shrink-0">2</span>
                        <span className="text-white/85">
                          Scroll and tap <b>Add to Home Screen</b>
                        </span>
                      </li>
                      <li className="flex items-start gap-3">
                        <span className="w-7 h-7 rounded-full bg-[#00f0ff]/15 text-[#00f0ff] flex items-center justify-center text-sm font-semibold flex-shrink-0">3</span>
                        <span className="text-white/85">
                          Tap <b>Add</b> in the top-right corner — done.
                        </span>
                      </li>
                    </ol>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right — perks / what you get */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-5">
              What you get
            </div>
            <div className="space-y-4">
              {perks.map((p) => (
                <div
                  key={p.title}
                  data-testid={`install-perk-${p.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 hover:border-[#00f0ff]/30 hover:bg-white/[0.05] transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[#00f0ff]/10 border border-[#00f0ff]/25 flex items-center justify-center flex-shrink-0">
                      <p.icon size={20} className="text-[#00f0ff]" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-[15px] mb-1">{p.title}</div>
                      <div className="text-[#a395c3] text-sm leading-relaxed">{p.body}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-2xl border border-white/10 bg-[#0a0510] p-5">
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-2">
                Privacy
              </div>
              <p className="text-sm text-white/70 leading-relaxed">
                The installed app is the exact same secure interface as the web dashboard. Nothing is downloaded from an app store, and no additional permissions are required beyond your admin login.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
